import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '#providers/api/index.ts';
import { getConversationsQueryKey } from '#providers/conversations/Conversations.helpers.ts';
import { useProjects } from '#providers/projects/index.ts';
import { getQueryLoadState } from '#providers/query/index.ts';
import type {
  TConversationList,
  TConversationListOptions,
} from './useConversationList.types.ts';

export const useConversationList = ({
  excludeEscalated = false,
  excludeGuidedExperience = false,
}: TConversationListOptions = {}): TConversationList => {
  const api = useApi();
  const { currentProjectId } = useProjects();

  const projectId = currentProjectId ?? '';

  const request = {
    project_id: projectId,
    ...(excludeEscalated ? { exclude_escalated: true } : {}),
    ...(excludeGuidedExperience ? { exclude_guided_experience: true } : {}),
  };

  const queryKey = getConversationsQueryKey(request);

  const query = useQuery({
    queryKey,
    queryFn: currentProjectId
      ? async () => {
          const { conversations } =
            await api.conversations.getUserConversations(request);

          return conversations;
        }
      : skipToken,
  });

  const conversations = useMemo(() => query.data ?? [], [query.data]);

  const { isLoading, loadError } = getQueryLoadState(query);

  return {
    conversations,
    isLoadingConversations: isLoading,
    conversationsError: loadError,
  };
};
